import { useCallback, useEffect, useRef, useState } from "react"
import ReactPlayer from "react-player"
import { Iframe } from "../Iframe/Iframe"

import s from "./VideoPlayer.module.scss"

// List of players that react-player supports
// https://github.com/cookpete/react-player?tab=readme-ov-file#supported-media
const CUSTOMIZED_PLAYERS = ["vimeo", "youtube", "youtu.be"]

type VideoPlayerProps = {
  src: string
  isSkipForwardDisabled: boolean
  onPlaythroughComplete: () => void
}
export const VideoPlayer = (props: VideoPlayerProps) => {
  const { src, onPlaythroughComplete } = props

  const isCustomizedPlayer = CUSTOMIZED_PLAYERS.some((player) =>
    src.includes(player)
  )

  useEffect(() => {
    if (!isCustomizedPlayer) {
      onPlaythroughComplete()
    }
  }, [isCustomizedPlayer, onPlaythroughComplete])

  if (isCustomizedPlayer) return <CustomPlayer {...props} />
  return <Iframe src={src} />
}

const CustomPlayer = ({
  src,
  isSkipForwardDisabled,
  onPlaythroughComplete,
}: VideoPlayerProps) => {
  // Watch for potential regression - initial value used to be 1.2
  // https://jira.attensi.com/browse/WEB-17416
  const secondsWatched = useRef(0)
  const playerRef = useRef<HTMLVideoElement | null>(null)
  const [player, setPlayer] = useState<HTMLVideoElement | null>(null)

  const setPlayerRef = useCallback((player: HTMLVideoElement) => {
    if (!player) return
    playerRef.current = player
    setPlayer(playerRef.current)
  }, [])

  const onTimeUpdate = () => {
    if (!player) return
    if (!isSkipForwardDisabled) return

    const playedSeconds = player.currentTime
    const playedPercentage = playedSeconds / player.duration

    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/timeupdate_event
    // If `timeupdate` event shows progress longer than defined value, we assume user
    // is seeking. This value should be higher than the `timeupdate` event frequency
    // - which depends on system load and measures between 4Hz (0.25s) and 66Hz (~0.15s),
    // assuming that no lag occurs - otherwise we risk false positives.
    // Less than 0.6 can cause stuttering on slow connections.
    const seekTreshold = 0.6
    const isPlayingForwards = secondsWatched.current < playedSeconds
    const isSeeking =
      Math.abs(secondsWatched.current - playedSeconds) > seekTreshold

    // Save timestamp only if video is playing forwards on its own
    if (isPlayingForwards && !isSeeking) {
      secondsWatched.current = playedSeconds
    }
    // Return to saved timestamp when user tries to seek ahead
    if (isPlayingForwards && isSeeking) {
      player.currentTime = secondsWatched.current
    }

    // Mark video as watched if user watched more than 95%
    if (!isSeeking && playedPercentage >= 0.95) {
      onPlaythroughComplete()
    }
  }

  return (
    <div className={s.iframe}>
      <ReactPlayer
        ref={setPlayerRef}
        src={src}
        width="100%"
        height="100%"
        onTimeUpdate={onTimeUpdate}
        autoPlay={false} // https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Autoplay
        controls={true}
        loop={false}
        muted={false}
        playsInline={true}
      />
    </div>
  )
}
